import * as React from "react";
import {
    Box,
    FormControl,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import {toast} from "react-toastify";
import Loading from "../../components/loading";
import MultiSelect from "../../components/multiSelect/multiSelect";
import {useAuth} from "../../providers/auth";
import {PermissionType} from "../../objects/UserRead";
import {RoleType} from "../../objects/Role";
import {
    BillablesBrokerRow,
    BillablesMonth,
    BillablesSchoolGroupRow,
    defaultBillablesWindow,
    findMonthStat,
    formatCount,
    formatMoney,
    getBillablesBrokers,
    getBillablesSchoolGroups,
    monthLabel,
    windowDateRange,
} from "./billablesService";
import AdjustBillablesModal, {AdjustBillablesContext} from "./adjustBillablesModal";
import AcceptedBillablesModal from "./acceptedBillablesModal";

const hatchSx = {
    backgroundImage:
        "repeating-linear-gradient(45deg, transparent, transparent 10px, #eee 10px, #eee 20px)",
};

type BrokerRowState = BillablesBrokerRow & {
    open?: boolean;
    loading?: boolean;
    groups?: BillablesSchoolGroupRow[];
};

export default function BillablesPage() {
    const auth = useAuth();
    const canAdmin = !!auth.user?.permissions?.includes(PermissionType.BillablesAdmin);
    const canAdjust =
        canAdmin && auth.user?.role?.type !== RoleType.SALES_REP;

    const initial = defaultBillablesWindow();
    const [year, setYear] = React.useState(initial.year);
    const [frontMonth, setFrontMonth] = React.useState(initial.frontMonth);
    const [brokers, setBrokers] = React.useState<BrokerRowState[]>([]);
    const [months, setMonths] = React.useState<BillablesMonth[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [companyItems, setCompanyItems] = React.useState<{id: number; value: string}[]>([]);
    const [selectedCompanies, setSelectedCompanies] = React.useState<{id: number; value: string}[]>([]);
    const [adjustCtx, setAdjustCtx] = React.useState<AdjustBillablesContext | null>(null);
    const [acceptedCtx, setAcceptedCtx] = React.useState<{
        hpCampaignId: number;
        companyId: number;
        brokerId: number;
    } | null>(null);
    const [reloadToken, setReloadToken] = React.useState(0);

    const range = windowDateRange(year, frontMonth);

    const load = React.useCallback(async () => {
        setLoading(true);
        const r = windowDateRange(year, frontMonth);
        const data = await getBillablesBrokers(r.startDate, r.endDate);
        if (!data) {
            toast.error("Unable to run report, please try again.");
            setBrokers([]);
            setMonths(r.monthsDisplay);
            setLoading(false);
            return;
        }
        setMonths(data.months?.length ? data.months : r.monthsDisplay);
        const rows = (data.brokers || []).map((b) => ({...b, open: false, groups: undefined}));
        setBrokers(rows);

        const byId = new Map<number, string>();
        rows.forEach((b) => byId.set(b.companyId, b.company));
        const companies = Array.from(byId.entries())
            .map(([id, value]) => ({id, value}))
            .sort((a, b) => a.value.localeCompare(b.value));
        setCompanyItems(companies);
        setSelectedCompanies(companies);
        setLoading(false);
    }, [year, frontMonth]);

    React.useEffect(() => {
        load();
    }, [load, reloadToken]);

    const searchCompanies = async (search: string, selected: any[]) => {
        const selectedIds = selected.map((x: any) => x.id);
        return companyItems.filter(
            (x) =>
                x.value.toLowerCase().includes(search.toLowerCase()) && !selectedIds.includes(x.id)
        );
    };

    const filteredBrokers = React.useMemo(() => {
        if (!selectedCompanies.length) {
            return [];
        }
        const ids = new Set(selectedCompanies.map((c) => c.id));
        return brokers.filter((b) => ids.has(b.companyId));
    }, [brokers, selectedCompanies]);

    const toggleOpen = async (row: BrokerRowState) => {
        const nextOpen = !row.open;
        setBrokers((prev) =>
            prev.map((b) =>
                b.brokerId === row.brokerId && b.companyId === row.companyId
                    ? {...b, open: nextOpen, loading: nextOpen, groups: nextOpen ? b.groups : undefined}
                    : b
            )
        );
        if (!nextOpen) {
            return;
        }
        const r = windowDateRange(year, frontMonth);
        const groups = await getBillablesSchoolGroups(r.startDate, r.endDate, row.brokerId, row.companyId);
        setBrokers((prev) =>
            prev.map((b) =>
                b.brokerId === row.brokerId && b.companyId === row.companyId
                    ? {...b, loading: false, groups}
                    : b
            )
        );
    };

    const reloadGroups = async (companyId: number, brokerId: number) => {
        const r = windowDateRange(year, frontMonth);
        setBrokers((prev) =>
            prev.map((b) =>
                b.brokerId === brokerId && b.companyId === companyId ? {...b, loading: true} : b
            )
        );
        const groups = await getBillablesSchoolGroups(r.startDate, r.endDate, brokerId, companyId);
        setBrokers((prev) =>
            prev.map((b) =>
                b.brokerId === brokerId && b.companyId === companyId
                    ? {...b, loading: false, groups, open: true}
                    : b
            )
        );
    };

    const cellBorder = {borderLeft: "2px solid #ccc"};

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h5" sx={{mb: 2}}>
                Billables
            </Typography>

            <Box sx={{display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                    <IconButton
                        size="small"
                        onClick={() => setYear((y) => y - 1)}
                        aria-label="Previous year"
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography sx={{minWidth: 48, textAlign: "center", fontWeight: 600}}>{year}</Typography>
                    <IconButton
                        size="small"
                        onClick={() => setYear((y) => y + 1)}
                        aria-label="Next year"
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Box>

                <FormControl size="small" sx={{minWidth: 140}}>
                    <InputLabel id="billables-front-month">Front Month</InputLabel>
                    <Select
                        labelId="billables-front-month"
                        label="Front Month"
                        value={frontMonth}
                        onChange={(e) => setFrontMonth(Number(e.target.value))}
                    >
                        {Array.from({length: 12}, (_, i) => i + 1).map((m) => (
                            <MenuItem key={m} value={m}>
                                {monthLabel(m)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Typography variant="body2" color="text.secondary">
                    {range.label}
                </Typography>

                <Box sx={{minWidth: 220}}>
                    <MultiSelect
                        searchFunction={searchCompanies}
                        selectedItems={selectedCompanies}
                        setSelectedItems={setSelectedCompanies}
                        placeholder="Companies"
                    />
                </Box>

                <Button
                    variant="contained"
                    color="success"
                    startIcon={<RefreshIcon />}
                    onClick={() => setReloadToken((t) => t + 1)}
                >
                    Reload
                </Button>
            </Box>

            {loading ? (
                <Loading />
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small" stickyHeader sx={{"& td, & th": {fontSize: 13, py: 0.75}}}>
                        <TableHead>
                            <TableRow sx={{"& th": {backgroundColor: "#ccc", fontWeight: 700}}}>
                                <TableCell sx={{width: 40}} />
                                <TableCell>Company</TableCell>
                                <TableCell>Broker</TableCell>
                                {months.map((month) => (
                                    <React.Fragment key={`h-${month.month}_${month.year}`}>
                                        <TableCell align="right" sx={cellBorder}>
                                            {month.month}/{month.year} GOOD
                                        </TableCell>
                                        <TableCell align="right">
                                            {month.month}/{month.year} BILLED
                                        </TableCell>
                                    </React.Fragment>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredBrokers.map((row) => (
                                <React.Fragment key={`${row.companyId}-${row.brokerId}`}>
                                    <TableRow hover>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => toggleOpen(row)}>
                                                {row.open ? (
                                                    <IndeterminateCheckBoxOutlinedIcon fontSize="small" />
                                                ) : (
                                                    <AddBoxOutlinedIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            {row.company} ({row.companyId})
                                        </TableCell>
                                        <TableCell>
                                            {row.broker} ({row.brokerId})
                                        </TableCell>
                                        {months.map((month) => {
                                            const cell = findMonthStat(row.months, month.month, month.year);
                                            const missing = cell?.missing ?? 0;
                                            return (
                                                <React.Fragment key={`b-${row.brokerId}-${month.month}_${month.year}`}>
                                                    <TableCell
                                                        align="right"
                                                        sx={{...cellBorder, ...(!cell ? hatchSx : {})}}
                                                    >
                                                        {cell?.good
                                                            ? `${formatMoney(cell.goodRev)} (${formatCount(cell.good)})`
                                                            : null}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            ...(!cell ? hatchSx : {}),
                                                            ...(cell && missing === 1
                                                                ? {backgroundColor: "#f8d7da"}
                                                                : {}),
                                                            ...(cell && missing === 2
                                                                ? {backgroundColor: "#fff3cd"}
                                                                : {}),
                                                        }}
                                                    >
                                                        {cell?.billable
                                                            ? `${formatMoney(cell.billableRev)} (${formatCount(cell.billable)})`
                                                            : null}
                                                    </TableCell>
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableRow>

                                    {row.open && row.loading && (
                                        <TableRow>
                                            <TableCell colSpan={3 + months.length * 2}>
                                                <Box sx={{py: 2, display: "flex", justifyContent: "center"}}>
                                                    <Loading />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {row.open &&
                                        !row.loading &&
                                        (row.groups || []).map((group) => (
                                            <TableRow key={`${row.companyId}-${row.brokerId}-${group.schoolGroupId}`} hover>
                                                <TableCell />
                                                <TableCell colSpan={2}>
                                                    Group: {group.school} ({group.schoolGroupId})
                                                </TableCell>
                                                {months.map((month) => {
                                                    const cell = findMonthStat(
                                                        group.months,
                                                        month.month,
                                                        month.year
                                                    );
                                                    return (
                                                        <React.Fragment
                                                            key={`g-${group.schoolGroupId}-${month.month}_${month.year}`}
                                                        >
                                                            <TableCell
                                                                align="right"
                                                                sx={{...cellBorder, ...(!cell ? hatchSx : {})}}
                                                            >
                                                                {canAdjust && cell?.good ? (
                                                                    <Link
                                                                        component="button"
                                                                        type="button"
                                                                        underline="hover"
                                                                        sx={{mr: 1, fontSize: 13}}
                                                                        onClick={() =>
                                                                            setAdjustCtx({
                                                                                school: group.school,
                                                                                schoolGroupId: group.schoolGroupId,
                                                                                companyId: row.companyId,
                                                                                brokerId: row.brokerId,
                                                                                year: month.year,
                                                                                month: month.month,
                                                                                expectedBilled: cell.goodRev,
                                                                            })
                                                                        }
                                                                    >
                                                                        Edit
                                                                    </Link>
                                                                ) : null}
                                                                {cell?.good
                                                                    ? `${formatMoney(cell.goodRev)} (${formatCount(cell.good)})`
                                                                    : null}
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                sx={{
                                                                    ...(!cell ? hatchSx : {}),
                                                                    ...(cell && cell.billable < 1
                                                                        ? {backgroundColor: "#f8d7da"}
                                                                        : {}),
                                                                }}
                                                            >
                                                                {canAdmin && cell ? (
                                                                    <Link
                                                                        component="button"
                                                                        type="button"
                                                                        underline="hover"
                                                                        sx={{fontSize: 13}}
                                                                        onClick={() =>
                                                                            setAcceptedCtx({
                                                                                hpCampaignId: group.hpCampaignId,
                                                                                companyId: row.companyId,
                                                                                brokerId: row.brokerId,
                                                                            })
                                                                        }
                                                                    >
                                                                        {cell.billable
                                                                            ? `${formatMoney(cell.billableRev)} (${formatCount(cell.billable)})`
                                                                            : `${formatMoney(0)} (0)`}
                                                                    </Link>
                                                                ) : cell ? (
                                                                    cell.billable
                                                                        ? `${formatMoney(cell.billableRev)} (${formatCount(cell.billable)})`
                                                                        : null
                                                                ) : null}
                                                            </TableCell>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <AdjustBillablesModal
                context={adjustCtx}
                onClose={() => setAdjustCtx(null)}
                onSaved={(ctx) => reloadGroups(ctx.companyId, ctx.brokerId)}
            />
            <AcceptedBillablesModal
                hpCampaignId={acceptedCtx?.hpCampaignId ?? null}
                onClose={() => setAcceptedCtx(null)}
                onSaved={() => {
                    if (acceptedCtx) {
                        reloadGroups(acceptedCtx.companyId, acceptedCtx.brokerId);
                    }
                }}
            />
        </Box>
    );
}
